// Shared schema + lookup for home-page module visibility. Used by the
// homepage, sub-menus (goods-in, conveyor, dispatch-menu), and the admin
// settings page so they all agree on which keys exist, what role
// defaults apply, and how to read a stored visibility map.
import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../api/apiFetch";
import { legacyRankOf, readUserPosition } from "../../access/positions";
import { useAccess } from "../../access/AccessContext";

export type Role = "superAdmin" | "admin" | "basic";

export type RoleVisibility = Partial<Record<Role, boolean>>;
export type HomeModuleVisibilityMap = Record<string, RoleVisibility>;

export const ROLES: readonly Role[] = ["superAdmin", "admin", "basic"];

// Module identifiers — parents are bare keys, children are dot-pathed
// under their parent. Defaults preserve the pre-settings behaviour:
// Put Away, Dispatch (and its children), and Book In are superAdmin-only;
// everything else is visible to all three roles.
export interface HomeModuleChild {
    key: string;        // dot-pathed: "goods_in.book_in"
    labelKey: string;   // i18n key reused from the actual button label
    defaults: Record<Role, boolean>;
}
export interface HomeModuleParent {
    key: string;
    labelKey: string;
    defaults: Record<Role, boolean>;
    children: readonly HomeModuleChild[];
}

const ALL_VISIBLE: Record<Role, boolean> = { superAdmin: true, admin: true, basic: true };
const SUPER_ONLY: Record<Role, boolean> = { superAdmin: true, admin: false, basic: false };

export const HOME_MODULES: readonly HomeModuleParent[] = [
    {
        key: "goods_in", labelKey: "goods_in", defaults: ALL_VISIBLE,
        children: [
            { key: "goods_in.inbound",    labelKey: "pallet_receiving", defaults: ALL_VISIBLE },
            { key: "goods_in.move",       labelKey: "move",             defaults: ALL_VISIBLE },
            { key: "goods_in.scan_sheet", labelKey: "scan_sheet",       defaults: ALL_VISIBLE },
            { key: "goods_in.relabel",    labelKey: "relabel",          defaults: ALL_VISIBLE },
            { key: "goods_in.book_in",    labelKey: "book_in",          defaults: SUPER_ONLY  },
            { key: "goods_in.pallets",    labelKey: "pallets",          defaults: ALL_VISIBLE },
            { key: "goods_in.sku_finder", labelKey: "sku_finder",       defaults: ALL_VISIBLE },
            { key: "goods_in.sourcing",   labelKey: "sourcing",         defaults: ALL_VISIBLE },
        ],
    },
    {
        key: "conveyor", labelKey: "conveyor", defaults: ALL_VISIBLE,
        children: [
            { key: "conveyor.collect",      labelKey: "collect",            defaults: ALL_VISIBLE },
            { key: "conveyor.add",          labelKey: "add",                defaults: ALL_VISIBLE },
            { key: "conveyor.holding",      labelKey: "holding_pallets",    defaults: ALL_VISIBLE },
            { key: "conveyor.manual_sort",  labelKey: "manual_sorter_scan", defaults: ALL_VISIBLE },
            { key: "conveyor.pallet_validator", labelKey: "pallet_validator", defaults: ALL_VISIBLE },
            { key: "conveyor.search",       labelKey: "search",             defaults: ALL_VISIBLE },
        ],
    },
    {
        key: "put_away", labelKey: "put_away", defaults: SUPER_ONLY,
        children: [],
    },
    {
        key: "stock_control", labelKey: "stock_control", defaults: ALL_VISIBLE,
        children: [
            { key: "stock_control.stock_check", labelKey: "stock_check", defaults: ALL_VISIBLE },
            { key: "stock_control.move_stock",  labelKey: "move_stock",  defaults: ALL_VISIBLE },
            { key: "stock_control.sku_check",   labelKey: "sku_check",   defaults: ALL_VISIBLE },
        ],
    },
    {
        key: "pick_face", labelKey: "pick_face", defaults: ALL_VISIBLE,
        children: [
            { key: "pick_face.shelf_drop",    labelKey: "shelf_drop",    defaults: ALL_VISIBLE },
            { key: "pick_face.forecast_drop", labelKey: "forecast_drop", defaults: ALL_VISIBLE },
            { key: "pick_face.pick_queue",    labelKey: "pick_queue",    defaults: ALL_VISIBLE },
            // Pick to tote is a deliberate grant: internal-order picks
            // that bypass dispatch shouldn't be one mis-tap away for
            // every picker, so it ships superAdmin-only and is switched
            // on per role in Settings › App Modules.
            { key: "pick_face.pick_to_tote",  labelKey: "pick_to_tote",  defaults: SUPER_ONLY  },
            // Flagged Picks (2026-10-08): leaders fetch missing-at-pick stock
            // from high racking. Assistant Team Leader and above via the
            // access matrix (pick_face.flagged_picks); superAdmin-only in
            // the legacy map, like Pick to Tote.
            { key: "pick_face.flagged_picks", labelKey: "flagged_picks", defaults: SUPER_ONLY  },
            // Pallet Picks (2026-10-30): pallet orders picked without a tote
            // and staged for the pallet desk. Rode the Pick queue key until
            // now, so App Modules had no toggle for it.
            { key: "pick_face.pallet_picks", labelKey: "pallet_picks",  defaults: ALL_VISIBLE },
        ],
    },
    {
        key: "zone_storage", labelKey: "zone_storage", defaults: ALL_VISIBLE,
        children: [],
    },
    {
        key: "dispatch", labelKey: "dispatch", defaults: SUPER_ONLY,
        children: [
            { key: "dispatch.start",   labelKey: "dispatch_start",   defaults: SUPER_ONLY },
            // Pallet desk (2026-10-30): book, label and release pallet
            // orders. Shared the pack-and-ship key until now, so App
            // Modules had no toggle for it.
            { key: "dispatch.pallets", labelKey: "pallet_dispatch",  defaults: SUPER_ONLY },
            // Conveyor unload (2026-10-14): parcel-to-courier-pallet check
            // at the end of the pack-desk conveyor. Its own key so an
            // unloader can be granted it without the pack bench.
            { key: "dispatch.unload",  labelKey: "dispatch_unload",  defaults: SUPER_ONLY },
            // Load lorry (2026-10-19): the forklift driver scans closed
            // pallets' labels onto the vehicle. Its own key: the driver is
            // neither packer nor unloader.
            { key: "dispatch.load",    labelKey: "dispatch_load",    defaults: SUPER_ONLY },
            // Found items (2026-10-20, Poland first): a leader books a found
            // unit onto the dispatch shelf for the open order that wants it.
            // ATL and above via the access matrix; switch on per warehouse.
            { key: "dispatch.found_items", labelKey: "dispatch_found_items", defaults: SUPER_ONLY },
            { key: "dispatch.flagged", labelKey: "dispatch_flagged", defaults: SUPER_ONLY },
        ],
    },
    {
        key: "wipe", labelKey: "wipe", defaults: ALL_VISIBLE,
        children: [],
    },
] as const;

const DEFAULTS_BY_KEY: Record<string, Record<Role, boolean>> = (() => {
    const out: Record<string, Record<Role, boolean>> = {};
    for (const p of HOME_MODULES) {
        out[p.key] = p.defaults;
        for (const c of p.children) out[c.key] = c.defaults;
    }
    return out;
})();

export const moduleDefault = (key: string, role: Role): boolean => {
    const entry = DEFAULTS_BY_KEY[key];
    if (entry && role in entry) return entry[role];
    // Unknown key — fail open so a future module that isn't yet in the
    // defaults table doesn't disappear.
    return true;
};

// Resolve visibility for a (key, role) pair against the saved map.
// Falls back to the per-key default when the map (or the role inside
// the entry) is missing, which is what guarantees a fresh warehouse
// gets today's behaviour without anyone touching settings.
export const isVisible = (
    map: HomeModuleVisibilityMap | undefined | null,
    key: string,
    role: Role,
): boolean => {
    const entry = map?.[key];
    if (entry && typeof entry === "object" && typeof entry[role] === "boolean") {
        return entry[role] as boolean;
    }
    return moduleDefault(key, role);
};

// Parent lookup for every child key, e.g. "dispatch.start" -> "dispatch".
// Built from HOME_MODULES so it stays in step with the schema.
const PARENT_OF_KEY: Record<string, string> = (() => {
    const out: Record<string, string> = {};
    for (const p of HOME_MODULES) {
        for (const c of p.children) out[c.key] = p.key;
    }
    return out;
})();

// True when the map has an EXPLICIT boolean for (key, role) — i.e. an
// operator actually set this toggle, rather than it falling through to a
// default. Lets the guard tell "deliberately off" from "never configured".
const hasExplicit = (
    map: HomeModuleVisibilityMap | undefined | null,
    key: string,
    role: Role,
): boolean => {
    const entry = map?.[key];
    return !!entry && typeof entry === "object" && typeof entry[role] === "boolean";
};

// Access decision for a ROUTE guard — like isVisible, but a child key with
// no explicit setting inherits its PARENT's setting before falling back to
// defaults. This matches how the Settings grid reads: turning on the parent
// "Dispatch" / "Goods In" row is expected to make the pages under it work,
// even though the child sub-toggles were never touched. An explicitly-set
// child still wins over its parent, so an operator can still switch one
// sub-page off. Deliberately NOT used by the Home/menu tiles (which call
// isVisible), so their per-key behaviour is unchanged.
export const isRouteAllowed = (
    map: HomeModuleVisibilityMap | undefined | null,
    key: string,
    role: Role,
): boolean => {
    if (hasExplicit(map, key, role)) {
        return isVisible(map, key, role);
    }
    const parent = PARENT_OF_KEY[key];
    if (parent && hasExplicit(map, parent, role)) {
        return isVisible(map, parent, role);
    }
    // No explicit child or parent setting — fall back to the child's own
    // default (which for e.g. put_away/dispatch.* is superAdmin-only).
    return moduleDefault(key, role);
};

// Fetch the per-warehouse visibility map once on mount. Returns the
// loaded map (or `null` while loading / on failure — callers should treat
// `null` as "use defaults", which `isVisible` already does).
export const useHomeModuleVisibility = (warehouse: string | undefined): HomeModuleVisibilityMap | null => {
    const [map, setMap] = useState<HomeModuleVisibilityMap | null>(null);
    // Once access control is ACTIVE on the backend and the context has answered,
    // the Home / sub-menu tiles follow the caller's grants instead of the
    // per-warehouse settings map: a tile the user cannot open is not shown.
    // The map is synthesised in the shape isVisible() already reads (one
    // boolean per role, all equal — the grant is per user, not per role), so
    // the pages themselves need no change. In legacy and shadow mode the
    // settings map stays authoritative, exactly as today.
    const access = useAccess();
    const synthesised = useMemo<HomeModuleVisibilityMap | null>(() => {
        if (access.mode !== "active" || !access.ready || access.failed) return null;
        const out: HomeModuleVisibilityMap = {};
        for (const p of HOME_MODULES) {
            const keys = [p.key, ...p.children.map(c => c.key)];
            for (const key of keys) {
                const granted = access.hasAccess(key);
                out[key] = { superAdmin: granted, admin: granted, basic: granted };
            }
        }
        return out;
    }, [access]);
    useEffect(() => {
        if (!warehouse) { setMap(null); return; }
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(
                    `/settings/${encodeURIComponent(warehouse)}`,
                );
                if (!res.ok) return;
                const data = await res.json();
                if (alive && data.home_modules_visible && typeof data.home_modules_visible === "object") {
                    setMap(data.home_modules_visible as HomeModuleVisibilityMap);
                }
            } catch {
                // keep null — defaults apply
            }
        })();
        return () => { alive = false; };
    }, [warehouse]);
    return synthesised ?? map;
};

// Per-warehouse cache of the settings fetch, shared by every caller for the
// life of the tab. The route guard runs on EVERY navigation, so an
// uncached fetch would re-hit GET /settings/:warehouse on each one; the
// map only changes when an admin edits Settings, so a page-load-lifetime
// cache is the right granularity. Stores the promise (not just the result)
// so concurrent mounts share one in-flight request rather than racing.
type VisibilityFetch = {
    promise: Promise<HomeModuleVisibilityMap | null>;
    settled: boolean;
    map: HomeModuleVisibilityMap | null;
    /** True when the fetch failed (network / non-2xx). Distinct from a
     *  successful load that genuinely had no map. The guard MUST NOT treat a
     *  failed load as "forbidden" — that locks a legitimate admin out of a
     *  module whose default is superAdmin-only, which is exactly the reported
     *  bug: the guard's mount-time /settings fetch failing (stale bearer,
     *  cold BE, transient 401) while a manual refetch succeeds. */
    failed: boolean;
};
const visibilityCache = new Map<string, VisibilityFetch>();

/**
 * Drop every cached map. Called on logout so the next sign-in re-reads
 * settings rather than inheriting the previous session's copy (the app
 * never does a full page load between logins, so nothing else would
 * clear it). Also the test seam — jest keeps module state within a file.
 */
export const __resetModuleVisibilityCache = (): void => {
    visibilityCache.clear();
};

const loadVisibility = (warehouse: string): VisibilityFetch => {
    const hit = visibilityCache.get(warehouse);
    if (hit) return hit;

    const entry: VisibilityFetch = { promise: null as never, settled: false, map: null, failed: false };
    entry.promise = (async () => {
        try {
            const res = await apiFetch(`/settings/${encodeURIComponent(warehouse)}`);
            if (!res.ok) { entry.failed = true; return null; }
            const data = await res.json();
            if (data?.home_modules_visible && typeof data.home_modules_visible === "object") {
                return data.home_modules_visible as HomeModuleVisibilityMap;
            }
            // Reached the server; it genuinely has no map. Cacheable.
            return null;
        } catch {
            // Network/parse failure — the guard must retry, not deny.
            entry.failed = true;
            return null;
        }
    })().then(map => {
        entry.map = map;
        entry.settled = true;
        // Don't let one transient blip pin this warehouse to the defaults
        // for the life of the tab: with put_away/dispatch defaulting to
        // superAdmin-only, that would re-create the very lockout this
        // module exists to fix. Drop the entry so the next navigation
        // retries; in-flight callers still get this promise's result.
        if (entry.failed) visibilityCache.delete(warehouse);
        return map;
    });

    visibilityCache.set(warehouse, entry);
    return entry;
};

/**
 * Like useHomeModuleVisibility, but cached across mounts and — crucially for
 * the route guard — distinguishes "still loading" from "loaded, no map".
 * A guard that can't tell those apart would bounce the user on a hard
 * refresh, before their settings had arrived.
 *
 * Pass `undefined` to skip fetching entirely (never loading, null map).
 */
// Bounded retries for a FAILED settings fetch before the guard gives up and
// falls back to defaults. A failed fetch must never present as "forbidden"
// on the first miss — that locks a legitimate admin out of a superAdmin-
// default module (the reported bug). Between attempts the guard shows the
// loader, never a denial.
const VISIBILITY_FETCH_RETRIES = 3;
const VISIBILITY_RETRY_MS = 400;

type VisibilityState = {
    map: HomeModuleVisibilityMap | null;
    loading: boolean;
    failed: boolean;
    /** Which warehouse the map/failed values below actually belong to. Guards
     *  the derived-state trap: on a re-render with a NEW warehouse, `state`
     *  still holds the OLD warehouse's values for one frame. `resolved` is only
     *  true when `for` matches the warehouse currently being asked about, so
     *  the guard shows the loader instead of deciding on stale/absent data. */
    for: string | undefined;
};

export const useModuleVisibility = (
    warehouse: string | undefined,
): { map: HomeModuleVisibilityMap | null; loading: boolean; failed: boolean; resolved: boolean } => {
    // Seed synchronously from the cache when we already have a real map for
    // THIS warehouse — no loader flash between pages. Any other case (no entry,
    // settled-empty, settled-failed, or a different warehouse) starts as
    // loading so the effect resolves it; never decide on a first-frame null.
    const initial = warehouse ? visibilityCache.get(warehouse) : undefined;
    const [state, setState] = useState<VisibilityState>(
        () => !warehouse
            ? { map: null, loading: false, failed: false, for: warehouse }
            : (initial?.settled && !initial.failed && initial.map
                ? { map: initial.map, loading: false, failed: false, for: warehouse }
                : { map: null, loading: true, failed: false, for: warehouse }),
    );

    useEffect(() => {
        if (!warehouse) {
            setState({ map: null, loading: false, failed: false, for: warehouse });
            return;
        }
        let alive = true;
        let timer: ReturnType<typeof setTimeout> | undefined;

        const attempt = (triesLeft: number) => {
            const entry = loadVisibility(warehouse);
            entry.promise.then(() => {
                if (!alive) return;
                if (!entry.failed) {
                    // Success — a real map, or a genuine empty map (200 with no
                    // home_modules_visible → guard applies per-key defaults).
                    setState({ map: entry.map, loading: false, failed: false, for: warehouse });
                    return;
                }
                // Failed. Retry on the loader (never deny); surface failed only
                // after exhausting retries so the guard can fail open.
                if (triesLeft > 0) {
                    timer = setTimeout(() => { if (alive) attempt(triesLeft - 1); }, VISIBILITY_RETRY_MS);
                } else {
                    setState({ map: null, loading: false, failed: true, for: warehouse });
                }
            });
        };

        const seed = loadVisibility(warehouse);
        if (seed.settled && !seed.failed) {
            setState({ map: seed.map, loading: false, failed: false, for: warehouse });
            return () => { alive = false; };
        }
        setState({ map: null, loading: true, failed: false, for: warehouse });
        attempt(VISIBILITY_FETCH_RETRIES);

        return () => { alive = false; if (timer) clearTimeout(timer); };
    }, [warehouse]);

    // resolved: the state's values are for the warehouse being asked about and
    // the fetch has settled (not loading). Until then the guard must wait.
    const resolved = state.for === warehouse && !state.loading;
    return { map: state.map, loading: state.loading, failed: state.failed, resolved };
};

// Module visibility (home_modules_visible) is STILL keyed by the three
// legacy ranks — that migrates in a later step of the access-control
// plan. Positions collapse onto their legacy rank via legacyRankOf() so
// this keeps returning exactly what it always has, for both legacy
// sessions and sessions that now carry one of the 9 positions.
export const readUserRole = (): Role => legacyRankOf(readUserPosition());
